import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";

import { mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { itemTypeObj } from "@/constants/items";
import { useModal } from "@/features/modal/ModalStackManager";
import { MultiBarcodeInput } from "@/features/multiBarcodeInput/MultiBarcodeInput";
import { getLotId } from "@/features/pigment/form/MultiPigmentInputForm";
import customAlert from "@/features/ui/alert/Alert";
import { InventoriesAutoComplete } from "@/features/ui/autoComplete/inventoryLot/inventory-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { MantineSelectableTable } from "@/features/ui/table/MantineSelectableTable";
import { useWorkActions } from "@/features/workByManagement/hooks/useWorkActions";
import { useInventoriesFormFetch } from "@/hooks/useInventoriesFormFetch";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button, Flex, NumberInput, Select, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  AuthSignupPost201ResponseEquipmentWorksInner,
  ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner,
  ProductionActionApiWorksBulkInputPutRequest,
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  WorksBulkInputPutRequestWorkLotPairsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef, MRT_Row } from "mantine-react-table";
import { useEffect, useMemo } from "react";

const Create = Object.assign({}, FormMain, {
  Button: Button,
  TextInput: TextInput,
  Select: Select,
  Table: MantineSelectableTable,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  InventoriesAutoComplete: InventoriesAutoComplete,
  LocationAutoComplete: LocationAutoComplete,
  NumberInput: NumberInput,
  BarcodeInput: MultiBarcodeInput,
});

type PageKeyType = "equipment" | "plan"; // pageKey의 가능한 값들을 정의합니다.

type ItemType<T extends PageKeyType> = T extends "equipment"
  ? AuthSignupPost201ResponseEquipmentWorksInner
  : ProductionPlansGet200ResponseRowsInnerWorksInner; // pageKey에 따른 item의 타입을 정의합니다.

interface WorkInputFormProps<T extends PageKeyType> {
  workRowData: ItemType<T>;
}

export function RawMaterialInputForm<T extends PageKeyType>(
  params: WorkInputFormProps<T>,
): JSX.Element {
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const actions = useWorkActions();

  const workData = params.workRowData;

  const targetLocationCode = (workData as ProductionPlansGet200ResponseRowsInnerWorksInner)
    ?.locationSetting?.toLocation?.code;

  const bomItemCodes = workData?.routingData?.routingBoms?.map((value) => value.itemCode);

  const routingBoms = workData?.routingData?.routingBoms;

  const convertedRoutingBoms = routingBoms?.map((bom) => ({
    itemType: bom.itemWithGrade?.itemType,
    itemName: bom.itemWithGrade?.name,
    gradeName: bom.itemWithGrade?.gradeName,
    itemCategoryName: bom.itemWithGrade?.itemCategoryName,
    abstractItemCode: bom.itemWithGrade?.abstractItemCode,
    itemCode: bom.itemCode,
    quantityNumerator: bom.quantityNumerator,
  }));

  const form = useForm({
    initialValues: {
      quantityNumerator: 0,
      predictTotalQuantity: 0,
      predictQuantity: 0,
      itemCode: "",
      sourceLocationCode: "",
      useBarcode: false,
      barcodeInput: "",
      worksBulkInputPutRequest: [],
    },
  });

  const { data, refetch } = useInventoriesFormFetch({
    itemCodes: form.values.itemCode ?? bomItemCodes,
    locationCode: form.values.sourceLocationCode,
    lotId: form.values.barcodeInput,
    // enabled: customFunctions.ADD_STANDARD_INFO_BOM_SEARCH_MENU_SETTING || Boolean(form.values.useBarcode && form.values.barcodeInput),
  });

  const { mutate: workInputMutate } = useMutation(
    (params: ProductionActionApiWorksBulkInputPutRequest) =>
      mutateInventories.workInputAll(params).mutationFn(undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        actions.setWorks((prevWorks) => []);
        closeModal(form.values);
        customAlert("원부자재 일괄투입에 성공하였습니다.", "원부자재 일괄투입 성공", "green");
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: "원부자재 일괄투입" }),
          "원부자재 일괄투입 실패",
          "red",
        );
      },
    },
  );

  const getActionsColumn = (): MRT_ColumnDef<any> => ({
    accessorKey: "actions",
    header: "Actions",
    Cell: ({ row }) => (
      <Button variant="outline" color="red" onClick={() => handleRowDelete(row)}>
        삭제
      </Button>
    ),
  });

  const columnsBom = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "itemType",
        header: "품목군",
        Cell: (rows) => (
          <div>{itemTypeObj[rows.row.original.itemType as keyof typeof itemTypeObj]}</div>
        ),
      },
      { accessorKey: "itemName", header: "품목명" },
      { accessorKey: "gradeName", header: "GRADE" },
      { accessorKey: "itemCategoryName", header: "COLOR" },
      { accessorKey: "abstractItemCode", header: "SHAPE" },
      { accessorKey: "quantityNumerator", header: "비율 (%)" },
    ],
    [],
  );

  // 순서 로트명 품목명 Grade Color 형태 필요 원자재량 투입량 Actions
  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "order",
        header: "순서",
        size: 1,
      },
      {
        accessorKey: "lotName",
        header: "로트명",
      },
      {
        accessorKey: "itemName",
        header: "품목명",
      },
      {
        accessorKey: "gradeName",
        header: "Grade",
      },
      {
        accessorKey: "itemCategoryName",
        header: "Color",
      },
      {
        accessorKey: "abstractItemCode",
        header: "형태",
      },
      {
        accessorKey: "predictQuantity",
        header: "필요 원자재량",
      },
      {
        accessorKey: "quantity",
        header: "투입량",
        Cell: ({ cell }) => {
          return (
            <NumberInput
              {...form.getInputProps(`worksBulkInputPutRequest.${cell.row.index}.quantity`)}
              value={Number(cell.getValue())}
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        Cell: ({ row }) => (
          <Button variant="outline" color="red" onClick={() => handleRowDelete(row)}>
            삭제
          </Button>
        ),
      },
    ],
    [],
  );

  const handleOnSubmit = () => {
    const request = form.values.worksBulkInputPutRequest.map(
      (data: WorksBulkInputPutRequestWorkLotPairsInner) => ({
        workId: workData?.id as number,
        lotId: Number(getLotId(data.lotId.toString())),
        sourceLocationCode: data.sourceLocationCode,
        targetLocationCode: data.targetLocationCode,
        quantity: String(data.quantity),
      }),
    );

    workInputMutate({
      worksBulkInputPutRequest: {
        workLotPairs: request,
      },
    });
  };

  const handleOnRowSelection = (selectedRows: any) => {
    form.setFieldValue("itemCode", selectedRows[0]?.itemCode);
    form.setFieldValue("quantityNumerator", selectedRows[0]?.quantityNumerator);
  };

  const handleRowDelete = (row: MRT_Row<any>) => {
    form.removeListItem("worksBulkInputPutRequest", row.index);
  };

  const refetchHandler = async () => {
    const data = await refetch();
    const inventoryData = data?.data?.data;
    const noData = !inventoryData?.length;
    if (noData) {
      form.setFieldValue("barcodeInput", "");
      customAlert(
        "존재하지 않은 로트입니다.",
        "바코드에 해당하는 로트가 존재하지 않습니다.",
        "red",
      );
    } else {
      const { lotId, itemCode, itemName, lot, locationCode, quantity, itemWithGrade } =
        inventoryData[0] as ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner;
      form.setFieldValue("barcodeInput", "");
      form.insertListItem("worksBulkInputPutRequest", {
        lotName: lot?.name,
        lotExpiration: lot?.expiration,
        lotId,
        itemCode,
        itemName,
        sourceLocationCode: locationCode,
        targetLocationCode: targetLocationCode,
        quantity,
        predictQuantity: form.values.predictQuantity,
        itemCategoryName: itemWithGrade?.itemCategoryName,
        abstractItemCode: itemWithGrade?.abstractItemCode,
        gradeName: itemWithGrade?.gradeName,
      });
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (!form.values.barcodeInput) return;
      if (checkDuplicateLot(form.values.barcodeInput)) return;
      await form.setFieldValue("barcodeInput", form.values.barcodeInput);
      await form.setFieldValue("useBarcode", true);
      await refetchHandler();
    }
  };

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (!inputValue) return;
    if (checkDuplicateLot(inputValue)) return;
    await form.setFieldValue("barcodeInput", inputValue);
    await form.setFieldValue("useBarcode", true);
    await refetchHandler();
  };

  const checkDuplicateLot = (value: string) => {
    if (
      form.values.worksBulkInputPutRequest.some(
        (data: any) => data.lotId === Number(getLotId(value)),
      )
    ) {
      customAlert("중복된 로트입니다.", "중복된 로트는 입력할 수 없습니다.", "red");
      form.setFieldValue("barcodeInput", "");
      return true;
    }
    return false;
  };

  const handleInventories = async (value: string | null) => {
    if (value === null) return;
    if (!checkDuplicateLot(value)) {
      const { lotId } = data?.data?.filter(
        (data) => data.lotId === Number(getLotId(value)),
      )[0] as ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner;
      await form.setFieldValue("barcodeInput", String(lotId));
      await form.setFieldValue("useBarcode", true);
      await refetchHandler();
    }
  };

  const rawMaterialTotalWeight = form.values.worksBulkInputPutRequest.reduce(
    (acc: number, cur: WorksBulkInputPutRequestWorkLotPairsInner) => {
      return acc + Number(cur.quantity);
    },
    0,
  );

  useEffect(() => {
    const itemCode = form.values.itemCode;
    const quantityNumerator = form.values.quantityNumerator;
    const predictTotalQuantity = form.values.predictTotalQuantity;

    if (itemCode && quantityNumerator && predictTotalQuantity) {
      const predictQuantity =
        Number(quantityNumerator) * Number(form.values.predictTotalQuantity) * 0.01;
      form.setFieldValue("predictQuantity", predictQuantity);
    }
  }, [form.values.itemCode, form.values.predictTotalQuantity]);

  return (
    <Create.Wrapper w={"100%"}>
      <Create.Table
        enableRowSelection={true}
        disabledCheckBox={!form.values.predictTotalQuantity}
        enableMultiRowSelection={false}
        columns={columnsBom}
        data={convertedRoutingBoms ?? []}
        onRowSelection={handleOnRowSelection}
      />
      <Flex justify={"space-between"} gap={"xl"}>
        <Create.NumberInput
          w={"100%"}
          withAsterisk
          label={"예상 생산총량"}
          {...form.getInputProps("predictTotalQuantity")}
        />
        <Create.LocationAutoComplete
          w={"100%"}
          withAsterisk
          label={"로케이션"}
          {...form.getInputProps("sourceLocationCode")}
        />
      </Flex>
      <Flex justify={"space-between"} gap={"xl"}>
        <Create.NumberInput
          w={"100%"}
          disabled
          label={"필요 원자재량"}
          value={form.values.predictQuantity}
        />
        <Create.InventoriesAutoComplete
          w={"100%"}
          disabled={!form.values.sourceLocationCode}
          label={"로트 정보"}
          value={form.values.barcodeInput}
          onChange={handleInventories}
          query={
            form.values.itemCode
              ? {
                  itemCode: form.values.itemCode,
                  locationCode: form.values.sourceLocationCode,
                  quantity: { $gt: 0 },
                }
              : {
                  itemCode: bomItemCodes,
                  locationCode: form.values.sourceLocationCode,
                  quantity: { $gt: 0 },
                }
          }
        />
      </Flex>
      <Flex justify={"space-between"}>
        <Create.BarcodeInput
          handleBlur={handleBlur}
          onKeyDown={handleKeyDown}
          value={form.values.barcodeInput}
          {...form.getInputProps("barcodeInput")}
          label={"바코드 정보 입력"}
          required
        />
        <Flex gap={"xl"} align={"center"}>
          <Text size={"md"} mt={"xl"}>
            {`원부자재 투입 총 무게: ${rawMaterialTotalWeight} ${workData?.item?.unitText || ""}`}
          </Text>
          <Create.TextInput
            withAsterisk
            disabled
            label={"입고 로케이션 코드"}
            value={targetLocationCode}
          />
        </Flex>
      </Flex>
      <Create.Table
        enableRowSelection={false}
        localization={{
          noRecordsToDisplay: "투입할 원부자재를 선택해주세요.",
        }}
        columns={columns}
        data={form.values.worksBulkInputPutRequest.map(
          (data: WorksBulkInputPutRequestWorkLotPairsInner, index: number) => ({
            order: index + 1,
            ...data,
          }),
        )}
      />
      <Create.ButtonBox>
        <Create.Button color="gray" onClick={closeModal}>
          취소
        </Create.Button>
        <Create.Button onClick={handleOnSubmit}>일괄 투입</Create.Button>
      </Create.ButtonBox>
    </Create.Wrapper>
  );
}
